import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CalendarClock, CheckCircle2, Circle, Loader2, Send, Sparkles, Save, Wand2, X, ArrowRight, Download, Eye, Star, Trash2, MailCheck } from "lucide-react";
import { api } from "@/lib/api";
import { formatDeadline } from "@/lib/deadline";

const CHECKLIST_LABELS = {
  predicted_grades:    { title: "Predicted grades",          hint: "Auto-flips once you fill in any grade on your profile." },
  english_test:        { title: "English test (if needed)",   hint: "Auto-flips if not required for this destination." },
  personal_statement:  { title: "Personal statement draft",   hint: "Generate a first draft below." },
  references:          { title: "≥1 reference lined up",      hint: "Invite a teacher via email — they don't need an account." },
  application_fee:     { title: "Application fee acknowledged", hint: "Tick when you know how the fee gets paid." },
  deadline:            { title: "Deadline confirmed",          hint: "Tick when you've noted the exact deadline in your calendar." },
};

const AUTO = new Set(["predicted_grades", "english_test"]);

export default function ApplicationDetailPage() {
  const { degreeId } = useParams();
  const [app, setApp]         = useState(null);
  const [busy, setBusy]       = useState(null);
  const [drafts, setDrafts]   = useState([]);
  const [maxVersions, setMaxVersions] = useState(5);
  const [drafting, setDrafting] = useState(false);
  const [refining, setRefining] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null); // number | null (null = latest)
  const [editText, setEditText]     = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [savedFlash, setSavedFlash] = useState(null); // "saved" | "unchanged" | null
  const [includeProfile, setIncludeProfile] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [teacher, setTeacher] = useState({ email: "", name: "", subject: "" });
  const [references, setReferences] = useState([]);
  const [maxReferences, setMaxReferences] = useState(3);
  const [error, setError]     = useState(null);
  const [psNudgeShow, setPsNudgeShow] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const [a, d, n, r] = await Promise.all([
        api.get(`/applications/${degreeId}`),
        api.get(`/applications/${degreeId}/statement/drafts`).catch(() => ({ data: { drafts: [] } })),
        api.get(`/wua-plus/nudges/ps_reviewer`).catch(() => ({ data: { show: false } })),
        api.get(`/applications/${degreeId}/references`).catch(() => ({ data: { references: [], max: 3 } })),
      ]);
      setApp(a.data);
      setReferences(r.data?.references || []);
      if (r.data?.max) setMaxReferences(r.data.max);
      const ds = d.data?.drafts || [];
      setDrafts(ds);
      if (d.data?.max_versions) setMaxVersions(d.data.max_versions);
      // Only surface the WUA+ nudge once at least one draft exists — the
      // "post draft save" moment. Backend still owns the show/hide state
      // so a page refresh doesn't resurrect a dismissed banner.
      setPsNudgeShow(!!n.data?.show && ds.length > 0);
      // If nothing selected yet OR the previously-selected version was
      // pruned, snap to the latest.
      const latest = ds[0]?.version ?? null;
      setSelectedVersion(prev =>
        prev != null && ds.some(x => x.version === prev) ? prev : latest
      );
      setEditText(ds[0]?.text || "");
      setSavedFlash(null);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Could not load.");
    }
  };
  useEffect(() => { load(); }, [degreeId]);

  const dismissPsNudge = async () => {
    setPsNudgeShow(false);
    try { await api.post(`/wua-plus/nudges/ps_reviewer/dismiss`, {}); }
    catch { /* fire-and-forget — banner already hidden client-side */ }
  };
  const clickPsNudge = () => {
    // Retire the nudge (fire-and-forget) so tomorrow's revisit doesn't
    // re-nag; the <Link> is what actually routes.
    setPsNudgeShow(false);
    api.post(`/wua-plus/nudges/ps_reviewer/click`, {}).catch(() => {});
  };

  const activeDraft = useMemo(
    () => drafts.find(d => d.version === selectedVersion) || drafts[0] || null,
    [drafts, selectedVersion],
  );

  // When the user picks a different version, load its text into the
  // editor. If they'd edited the previous version without saving,
  // that unsaved text is discarded — expected UX for a version list.
  useEffect(() => {
    if (activeDraft) setEditText(activeDraft.text || "");
  }, [activeDraft?.version]);   // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = activeDraft ? (editText || "").trim() !== (activeDraft.text || "").trim() : (editText || "").trim().length > 0;

  const toggle = async (item) => {
    if (AUTO.has(item)) return;
    setBusy(item);
    try {
      const cur = app.checklist[item]?.checked;
      await api.post(`/applications/${degreeId}/checklist`, { item, checked: !cur });
      await load();
    } finally { setBusy(null); }
  };

  const draftStatement = async () => {
    setDrafting(true); setError(null);
    try {
      await api.post(`/applications/${degreeId}/statement/draft`, {});
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Draft failed.");
    } finally { setDrafting(false); }
  };

  const refineStatement = async () => {
    const draft = (editText || "").trim();
    if (!draft) {
      setError("Write a draft first — Refine needs your own text to polish.");
      return;
    }
    setRefining(true); setError(null);
    try {
      await api.post(`/applications/${degreeId}/statement/refine`, { text: draft });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Refine failed.");
    } finally { setRefining(false); }
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    setSavingEdit(true); setError(null); setSavedFlash(null);
    try {
      const r = await api.post(`/applications/${degreeId}/statement/save`, { text: editText });
      setSavedFlash(r.data?.unchanged ? "unchanged" : "saved");
      await load();
      setTimeout(() => setSavedFlash(null), 4000);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Save failed.");
    } finally { setSavingEdit(false); }
  };

  const inviteTeacher = async () => {
    if (!teacher.email.trim()) return;
    setBusy("references"); setError(null);
    try {
      await api.post(`/applications/${degreeId}/references/invite`, {
        teacher_email: teacher.email.trim(),
        teacher_name: teacher.name.trim() || undefined,
        subject: teacher.subject.trim() || undefined,
      });
      setTeacher({ email: "", name: "", subject: "" });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Invite failed.");
    } finally { setBusy(null); }
  };

  const cancelReference = async (email) => {
    if (!window.confirm(`Cancel the invite to ${email}? They can't submit after this.`)) return;
    setBusy("references"); setError(null);
    try {
      await api.delete(`/applications/${degreeId}/references`, { params: { teacher_email: email } });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Cancel failed.");
    } finally { setBusy(null); }
  };

  const pdfUrl = useMemo(() => {
    // Direct link that includes the axios credential cookie so we get
    // a browser download without an intermediate token.
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const qs = includeProfile ? "" : "?include_profile=false";
    return `${base}/api/applications/${degreeId}/export.pdf${qs}`;
  }, [degreeId, includeProfile]);

  // Same PDF, but with `inline=true` so the browser renders it inside
  // the preview drawer's iframe instead of triggering a download.
  const pdfPreviewUrl = useMemo(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const parts = ["inline=true"];
    if (!includeProfile) parts.push("include_profile=false");
    return `${base}/api/applications/${degreeId}/export.pdf?${parts.join("&")}`;
  }, [degreeId, includeProfile]);

  const deadline = useMemo(
    () => formatDeadline(app?.degree?.application_deadline),
    [app?.degree?.application_deadline],
  );

  // Close preview on Escape for keyboard-first users.
  useEffect(() => {
    if (!previewOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setPreviewOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [previewOpen]);


  if (!app) {
    return (
      <div className="max-w-4xl mx-auto p-6" data-testid="application-loading">
        {error ? error : <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</>}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 pb-24" data-testid="application-page">
      <Link to="/shortlist" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back to shortlist
      </Link>
      <div className="mb-6">
        <div className="label-uppercase text-[10px] text-stone-500 mb-1">Application</div>
        <h1 className="text-2xl font-semibold text-stone-900">{app.degree?.title || app.degree?.official_title}</h1>
        <div className="text-sm text-stone-600">{app.university?.name} · {app.university?.country}</div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span
            data-testid="application-status"
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              app.status === "ready" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
            }`}
          >{app.status.toUpperCase()}</span>
          {deadline && (
            <span
              data-testid="application-deadline-chip"
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                deadline.past
                  ? "bg-stone-100 text-stone-500 line-through"
                  : deadline.urgent
                    ? "bg-rose-100 text-rose-800"
                    : "bg-stone-100 text-stone-700"
              }`}
              title={`Deadline ${deadline.pretty}`}
            >
              <CalendarClock className="w-3 h-3" />
              <span>
                Deadline: {deadline.pretty}
                {deadline.relative && <> · {deadline.relative}</>}
              </span>
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 border border-rose-200 bg-rose-50 text-rose-900 text-sm rounded-md p-3">{error}</div>
      )}

      <section className="border border-stone-200 rounded-xl bg-white p-5 mb-6" data-testid="application-checklist">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h2 className="text-[15px] font-semibold text-stone-900">Checklist</h2>
          <label
            className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-stone-600 cursor-pointer select-none"
            title="Some universities disallow attaching a personal profile snapshot"
          >
            <input
              type="checkbox"
              className="accent-emerald-600"
              checked={includeProfile}
              data-testid="fast-apply-pdf-include-profile"
              onChange={e => setIncludeProfile(e.target.checked)}
            />
            <span>Include profile page</span>
          </label>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            data-testid="fast-apply-pdf-preview-btn"
            className="inline-flex items-center gap-1 rounded-md border border-stone-300 bg-white hover:bg-stone-50 text-stone-700 text-[12.5px] font-medium px-3 py-1.5"
            title="Preview the PDF before downloading"
          >
            <Eye className="w-3.5 h-3.5" />
            Preview
          </button>
          <a
            href={pdfUrl}
            data-testid="fast-apply-pdf-btn"
            className="inline-flex items-center gap-1 rounded-md bg-[#4C1D95] hover:bg-[#3B0764] text-white text-[12.5px] font-medium px-3 py-1.5"
            title="Download this application as a PDF package"
          >
            <Download className="w-3.5 h-3.5" />
            Download PDF
          </a>
        </div>
        <ul className="space-y-2">
          {Object.entries(CHECKLIST_LABELS).map(([k, meta]) => {
            const it = app.checklist[k] || {};
            const isAuto = AUTO.has(k);
            const canToggle = !isAuto;
            return (
              <li key={k}>
                <button
                  type="button"
                  onClick={() => canToggle && toggle(k)}
                  disabled={!canToggle || busy === k}
                  data-testid={`checklist-item-${k}`}
                  className={`w-full flex items-start gap-3 rounded-md p-2 -m-2 text-left ${
                    canToggle ? "hover:bg-stone-50" : "cursor-default"
                  }`}
                >
                  {it.checked
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-none mt-0.5" />
                    : <Circle className="w-5 h-5 text-stone-300 flex-none mt-0.5" />
                  }
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-medium ${it.checked ? "text-stone-900" : "text-stone-800"}`}>
                      {meta.title}
                      {isAuto && <span className="ml-2 text-[10px] uppercase tracking-wider text-stone-400">auto</span>}
                    </div>
                    <div className="text-[12px] text-stone-500 mt-0.5">{meta.hint}</div>
                    {it.note && <div className="text-[11px] text-emerald-700 mt-0.5">{it.note}</div>}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Personal statement draft + editor */}
      <section className="border border-stone-200 rounded-xl bg-white p-5 mb-6" data-testid="statement-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-[15px] font-semibold text-stone-900">Personal statement</h2>
          <div className="flex items-center gap-2">
            {drafts.length > 1 && (
              <select
                value={selectedVersion ?? ""}
                onChange={e => setSelectedVersion(Number(e.target.value))}
                data-testid="statement-version-select"
                className="border border-stone-300 rounded-md px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {drafts.map(d => (
                  <option key={d.version} value={d.version}>
                    v{d.version} · {d.source === "ai" ? "AI" : d.source === "ai-refine" ? "AI refine" : "You"} · {new Date(d.created_at).toLocaleDateString()}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={refineStatement}
              disabled={refining || drafting || !editText.trim()}
              data-testid="statement-refine-btn"
              title="AI pass over your current draft — tightens flow and grammar without inventing new content"
              className="inline-flex items-center gap-1 bg-white border border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:border-stone-200 disabled:text-stone-400 disabled:bg-stone-50 text-sm px-3 py-1.5 rounded-md"
            >
              <Wand2 className="w-4 h-4" />
              {refining ? "Refining…" : "Refine"}
            </button>
            <button
              type="button"
              onClick={draftStatement}
              disabled={drafting || refining}
              data-testid="statement-draft-btn"
              className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white text-sm px-3 py-1.5 rounded-md"
            >
              <Sparkles className="w-4 h-4" />
              {drafting ? "Drafting…" : drafts.length ? "Regenerate" : "Generate first draft"}
            </button>
          </div>
        </div>

        {drafts.length === 0 ? (
          <div data-testid="statement-empty" className="space-y-3">
            <div className="text-[13px] text-stone-500 leading-relaxed">
              Two ways to start: click <span className="font-semibold">Generate first draft</span> for an AI
              starter grounded in your profile, or write your own below and hit
              <span className="font-semibold"> Save as new version</span>.
            </div>
            <textarea
              rows={16}
              value={editText}
              onChange={e => { setEditText(e.target.value); if (savedFlash) setSavedFlash(null); }}
              data-testid="statement-editor"
              className="w-full border border-stone-300 rounded-md px-3 py-2 text-[13px] leading-relaxed font-serif focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Start writing your personal statement here…"
            />
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={saveEdit}
                disabled={savingEdit || !editText.trim()}
                data-testid="statement-save-btn"
                className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white text-sm px-3 py-1.5 rounded-md"
              >
                <Save className="w-4 h-4" />
                {savingEdit ? "Saving…" : "Save as new version"}
              </button>
              {savedFlash === "saved" && (
                <span className="text-[12px] text-emerald-700" data-testid="statement-save-flash">
                  Saved as version 1.
                </span>
              )}
              <span className="ml-auto font-mono text-[11px] text-stone-500">
                {(editText || "").length} chars
              </span>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-[11px] text-stone-500 mb-2 flex items-center gap-2" data-testid="statement-active-version">
              <span>
                Editing <span className="font-semibold text-stone-700">v{activeDraft?.version}</span>
                {" · "}
                {activeDraft?.source === "ai" ? "AI-generated" : "your edit"}
                {" · "}
                {activeDraft?.created_at && new Date(activeDraft.created_at).toLocaleString()}
              </span>
              <span className="ml-auto font-mono text-stone-500">
                {(editText || "").length} chars
              </span>
            </div>
            <textarea
              rows={16}
              value={editText}
              onChange={e => { setEditText(e.target.value); if (savedFlash) setSavedFlash(null); }}
              data-testid="statement-editor"
              className="w-full border border-stone-300 rounded-md px-3 py-2 text-[13px] leading-relaxed font-serif focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Edit your personal statement here…"
            />

            {psNudgeShow && (
              <div
                data-testid="ps-wuaplus-nudge"
                className="mt-2 flex items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2"
              >
                <Sparkles className="w-4 h-4 text-emerald-700 flex-none" />
                <div className="flex-1 min-w-0 text-[12.5px] leading-snug">
                  <span className="font-semibold text-emerald-900">Get expert eyes on this.</span>
                  <span className="text-emerald-800">
                    {" "}A human admissions officer reviews your statement within
                    <strong> 48 hours</strong> with line-level comments.
                  </span>
                </div>
                <Link
                  to="/wua-plus?intent=ps_review"
                  onClick={clickPsNudge}
                  data-testid="ps-wuaplus-nudge-cta"
                  className="inline-flex items-center gap-1 rounded-full bg-[#4C1D95] hover:bg-[#3B0764] text-white text-[12px] font-medium px-3 py-1 flex-none"
                >
                  See WUA+ review <ArrowRight className="w-3 h-3" />
                </Link>
                <button
                  type="button"
                  onClick={dismissPsNudge}
                  aria-label="Dismiss WUA+ nudge"
                  data-testid="ps-wuaplus-nudge-dismiss"
                  className="text-emerald-700 hover:text-emerald-900 flex-none"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <button
                type="button"
                onClick={saveEdit}
                disabled={savingEdit || !dirty}
                data-testid="statement-save-btn"
                title={
                  savingEdit
                    ? "Saving…"
                    : dirty
                      ? "Save this text as a new version"
                      : "Edit the text first — Save creates a new version when you've made changes"
                }
                className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 disabled:cursor-not-allowed text-white text-sm px-3 py-1.5 rounded-md"
              >
                <Save className="w-4 h-4" />
                {savingEdit ? "Saving…" : dirty ? "Save as new version" : "Nothing to save"}
              </button>
              {!dirty && !savedFlash && (
                <span
                  data-testid="statement-save-hint"
                  className="text-[12px] text-stone-500 italic"
                >
                  Edit the text above to enable saving. Or click <span className="font-semibold not-italic">Regenerate</span> for a fresh AI draft.
                </span>
              )}
              {savedFlash === "saved" && (
                <span className="text-[12px] text-emerald-700" data-testid="statement-save-flash">
                  Saved as new version.
                </span>
              )}
              {savedFlash === "unchanged" && (
                <span className="text-[12px] text-stone-500" data-testid="statement-save-flash">
                  No changes — nothing to save.
                </span>
              )}
              <span className="text-[11px] text-stone-500 ml-auto">
                Version history capped at {maxVersions} — oldest gets pruned when you save a new one.
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Reference invites — up to 1 primary + 2 backups */}
      <section className="border border-stone-200 rounded-xl bg-white p-5" data-testid="reference-section">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h2 className="text-[15px] font-semibold text-stone-900">Academic references</h2>
          <span className="text-[11px] text-stone-500">
            {references.length}/{maxReferences} · UK UCAS needs 1, US/postgrad usually 2
          </span>
        </div>

        {references.length > 0 && (
          <ul className="space-y-2 mb-4" data-testid="reference-list">
            {references.map((r) => {
              const received = !!r.consumed_at;
              return (
                <li
                  key={r.teacher_email}
                  data-testid={`reference-row-${r.teacher_email}`}
                  className={`rounded-md border p-3 ${
                    received ? "border-emerald-200 bg-emerald-50" : "border-stone-200 bg-stone-50"
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {received ? (
                      <MailCheck className="w-4 h-4 text-emerald-700 flex-none" />
                    ) : (
                      <Circle className="w-4 h-4 text-stone-400 flex-none" />
                    )}
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-stone-900">
                        {r.teacher_name || r.teacher_email}
                        {r.is_primary && (
                          <span
                            data-testid={`reference-primary-${r.teacher_email}`}
                            className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold px-1.5 py-0.5 align-middle"
                          >
                            <Star className="w-2.5 h-2.5" /> Primary
                          </span>
                        )}
                      </div>
                      <div className="text-[11.5px] text-stone-500 truncate">
                        {r.teacher_email}
                        {r.subject && <> · {r.subject}</>}
                      </div>
                    </div>
                    <span className="ml-auto text-[11px] text-stone-500">
                      {received
                        ? `Received ${new Date(r.consumed_at).toLocaleDateString()}`
                        : `Invited ${new Date(r.requested_at).toLocaleDateString()} · pending`}
                    </span>
                    {!received && (
                      <button
                        type="button"
                        onClick={() => cancelReference(r.teacher_email)}
                        disabled={busy === "references"}
                        aria-label={`Cancel invite to ${r.teacher_email}`}
                        data-testid={`reference-cancel-${r.teacher_email}`}
                        className="text-stone-500 hover:text-rose-700 disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {received && r.submitted_reference?.body && (
                    <div className="mt-2 rounded-md bg-white border border-emerald-200 p-3">
                      {r.submitted_reference.subject_taught && (
                        <div className="text-[10.5px] uppercase tracking-wider text-stone-500 mb-1">
                          Subject taught: {r.submitted_reference.subject_taught}
                        </div>
                      )}
                      <div
                        data-testid={`reference-body-${r.teacher_email}`}
                        className="text-[13px] text-stone-800 leading-relaxed whitespace-pre-wrap"
                      >
                        {r.submitted_reference.body}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {references.length < maxReferences ? (
          <div>
            <div className="text-[12px] text-stone-600 mb-2">
              {references.length === 0
                ? "Invite your primary referee first — they'll get a magic link (valid 14 days). No account needed."
                : "Add a backup in case your primary teacher goes silent — one of the most common reasons applications stall."}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="email" placeholder="teacher@school.edu" value={teacher.email}
                onChange={e => setTeacher(t => ({ ...t, email: e.target.value }))}
                data-testid="reference-teacher-email"
                className="border border-stone-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="text" placeholder="Teacher's name (optional)" value={teacher.name}
                onChange={e => setTeacher(t => ({ ...t, name: e.target.value }))}
                data-testid="reference-teacher-name"
                className="border border-stone-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="text" placeholder="Subject (e.g. A-level Maths)" value={teacher.subject}
                onChange={e => setTeacher(t => ({ ...t, subject: e.target.value }))}
                data-testid="reference-teacher-subject"
                className="border border-stone-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <button
                type="button"
                onClick={inviteTeacher}
                disabled={!teacher.email.trim() || busy === "references"}
                data-testid="reference-invite-btn"
                className="inline-flex items-center justify-center gap-1 sm:col-span-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white text-sm px-3 py-2 rounded-md"
              >
                <Send className="w-4 h-4" />
                {busy === "references"
                  ? "Sending…"
                  : references.length === 0 ? "Send primary invite" : "Send backup invite"}
              </button>
            </div>
          </div>
        ) : (
          <div
            data-testid="reference-cap-reached"
            className="text-[12px] text-stone-500 border border-stone-200 rounded-md p-3 bg-stone-50"
          >
            You've used all {maxReferences} slots. Cancel one above if you need to swap a referee.
          </div>
        )}
      </section>

      {/* PDF preview drawer — right-side overlay. Iframe pulls the
          `?inline=true` PDF so the browser renders it in-place rather
          than triggering a download. */}
      {previewOpen && (
        <div
          data-testid="pdf-preview-drawer"
          className="fixed inset-0 z-50 flex"
          role="dialog"
          aria-modal="true"
          aria-label="PDF preview"
        >
          <button
            type="button"
            onClick={() => setPreviewOpen(false)}
            aria-label="Close preview"
            data-testid="pdf-preview-backdrop"
            className="flex-1 bg-stone-900/40 hover:bg-stone-900/50 transition-colors"
          />
          <aside className="w-full sm:w-[640px] lg:w-[720px] h-full bg-white shadow-2xl flex flex-col">
            <header className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 flex-none">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                  Preview
                </div>
                <div className="text-[13px] font-semibold text-stone-900 truncate">
                  {app.degree?.title || app.degree?.official_title}
                </div>
              </div>
              <a
                href={pdfUrl}
                data-testid="pdf-preview-download"
                className="ml-auto inline-flex items-center gap-1 rounded-md bg-[#4C1D95] hover:bg-[#3B0764] text-white text-[12.5px] font-medium px-3 py-1.5"
                onClick={() => setPreviewOpen(false)}
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </a>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                aria-label="Close preview"
                data-testid="pdf-preview-close"
                className="rounded-md p-1.5 text-stone-500 hover:bg-stone-100 hover:text-stone-900"
              >
                <X className="w-4 h-4" />
              </button>
            </header>
            <iframe
              key={pdfPreviewUrl}
              title="Fast-Apply PDF preview"
              src={pdfPreviewUrl}
              data-testid="pdf-preview-iframe"
              className="flex-1 border-0 bg-stone-100"
            />
          </aside>
        </div>
      )}

    </div>
  );
}
